import { createStore } from 'vuex'
import axios from 'axios'
import logger from '../utils/logger'

// Create axios instance
const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json'
  }
})

// Cache management
const cache = {
  data: new Map(),
  timestamps: new Map(),
  maxAge: 5 * 60 * 1000, // 5 minutes
  maxSize: 100
}

// Cache utility functions
const cacheUtils = {
  set(key, data) {
    if (cache.data.size >= cache.maxSize) {
      const firstKey = cache.data.keys().next().value
      cache.data.delete(firstKey)
      cache.timestamps.delete(firstKey)
    }
    cache.data.set(key, data)
    cache.timestamps.set(key, Date.now())
  },
  
  get(key) {
    const timestamp = cache.timestamps.get(key)
    if (!timestamp || Date.now() - timestamp > cache.maxAge) {
      cache.data.delete(key)
      cache.timestamps.delete(key)
      return null
    }
    return cache.data.get(key)
  },
  
  clear() {
    cache.data.clear()
    cache.timestamps.clear()
  },
  
  invalidate(pattern) {
    for (const key of cache.data.keys()) {
      if (key.includes(pattern)) {
        cache.data.delete(key)
        cache.timestamps.delete(key)
      }
    }
  }
}

// Add token to requests if available
api.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle token refresh
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        try {
          const response = await api.post('/token/refresh/', {
            refresh: refreshToken
          })
          
          localStorage.setItem('access_token', response.data.access)
          api.defaults.headers.Authorization = `Bearer ${response.data.access}`
          
          return api(originalRequest)
        } catch (refreshError) {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          window.location.href = '/login'
        }
      }
    }
    
    return Promise.reject(error)
  }
)

export default createStore({
  state: {
    currentUser: null,
    isAuthenticated: !!localStorage.getItem('access_token'),
    tests: [],
    testTypes: [],
    sessions: [],
    results: [],
    users: [],
    recentActivities: [],
    loading: false,
    error: null,
    // Version info з fallback значеннями
    version: 'v0.2.2-alpha',
    buildDate: '2025-08-10',
    // Theme state
    currentTheme: localStorage.getItem('theme') || 'light',
    // SPA state
    lastUpdate: {},
    autoRefresh: true,
    refreshInterval: 30000, // 30 seconds
    refreshTimer: null,
    isOnline: navigator.onLine,
    pendingActions: []
  },
  
  mutations: {
    SET_CURRENT_USER(state, user) {
      state.currentUser = user
    },
    SET_AUTHENTICATED(state, status) {
      state.isAuthenticated = status
    },
    SET_TESTS(state, tests) {
      state.tests = tests
    },
    SET_TEST_TYPES(state, testTypes) {
      state.testTypes = testTypes
    },
    SET_SESSIONS(state, sessions) {
      state.sessions = sessions
    },
    SET_RESULTS(state, results) {
      state.results = results
    },
    SET_USERS(state, users) {
      state.users = users
    },
    SET_RECENT_ACTIVITIES(state, activities) {
      state.recentActivities = activities
    },
    SET_LOADING(state, status) {
      state.loading = status
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    // SPA mutations
    SET_LAST_UPDATE(state, { key, timestamp }) {
      state.lastUpdate[key] = timestamp
    },
    SET_AUTO_REFRESH(state, status) {
      state.autoRefresh = status
    },
    SET_REFRESH_TIMER(state, timer) {
      if (state.refreshTimer) {
        clearInterval(state.refreshTimer)
      }
      state.refreshTimer = timer
    },
    SET_ONLINE_STATUS(state, status) {
      state.isOnline = status
    },
    ADD_PENDING_ACTION(state, action) {
      state.pendingActions.push(action)
    },
    CLEAR_PENDING_ACTIONS(state) {
      state.pendingActions = []
    },
    SET_VERSION_INFO(state, { version, build_date }) {
      state.version = version;
      state.buildDate = build_date;
    },
    SET_THEME(state, theme) {
      state.currentTheme = theme;
      localStorage.setItem('theme', theme);
    }
  },
  
  actions: {
    // SPA Management Actions
    async initializeSPA({ commit, dispatch }) {
      // Set up online/offline detection
      window.addEventListener('online', () => {
        commit('SET_ONLINE_STATUS', true)
        dispatch('processPendingActions')
      })
      
      window.addEventListener('offline', () => {
        commit('SET_ONLINE_STATUS', false)
      })
      
      // Fetch version info
      await dispatch('fetchVersionInfo')
      
      // Start auto-refresh if authenticated
      if (this.state.isAuthenticated) {
        dispatch('startAutoRefresh')
      }
    },
    
    startAutoRefresh({ commit, dispatch, state }) {
      if (state.refreshTimer) {
        clearInterval(state.refreshTimer)
      }
      
      const timer = setInterval(async () => {
        if (state.autoRefresh && state.isAuthenticated && state.isOnline) {
          await dispatch('refreshAllData')
        }
      }, state.refreshInterval)
      
      commit('SET_REFRESH_TIMER', timer)
    },
    
    stopAutoRefresh({ commit }) {
      commit('SET_REFRESH_TIMER', null)
    },
    
    async refreshAllData({ dispatch, state }) {
      if (!state.isAuthenticated) return
      
      try {
        await Promise.allSettled([
          dispatch('fetchTests'),
          dispatch('fetchSessions'),
          dispatch('fetchResults'),
          dispatch('fetchRecentActivities'),
          dispatch('fetchVersionInfo')
        ])
      } catch (error) {
        logger.error('Error refreshing data:', error)
      }
    },
    
    async processPendingActions({ commit, state }) {
      if (state.pendingActions.length === 0) return
      
      for (const action of state.pendingActions) {
        try {
          await this.dispatch(action.type, action.payload)
        } catch (error) {
          logger.error('Error processing pending action:', error)
        }
      }
      
      commit('CLEAR_PENDING_ACTIONS')
    },
    
    // Cached API actions
    async fetchTestsWithCache({ commit }) {
      const cacheKey = 'tests'
      const cached = cacheUtils.get(cacheKey)
      
      if (cached) {
        commit('SET_TESTS', cached)
        return cached
      }
      
      try {
        commit('SET_LOADING', true)
        const response = await api.get('/test-types/')
        const data = response.data.results || response.data
        
        cacheUtils.set(cacheKey, data)
        commit('SET_TESTS', data)
        commit('SET_LAST_UPDATE', { key: 'tests', timestamp: Date.now() })
        
        return data
      } catch (error) {
        commit('SET_ERROR', 'Помилка завантаження тестів')
        logger.error('Error fetching tests:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchSessionsWithCache({ commit }) {
      const cacheKey = 'sessions'
      const cached = cacheUtils.get(cacheKey)
      
      if (cached) {
        commit('SET_SESSIONS', cached)
        return cached
      }
      
      try {
        commit('SET_LOADING', true)
        const response = await api.get('/sessions/')
        const data = response.data.results || response.data
        
        cacheUtils.set(cacheKey, data)
        commit('SET_SESSIONS', data)
        commit('SET_LAST_UPDATE', { key: 'sessions', timestamp: Date.now() })
        
        return data
      } catch (error) {
        commit('SET_ERROR', 'Помилка завантаження сесій')
        logger.error('Error fetching sessions:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchResultsWithCache({ commit }) {
      const cacheKey = 'results'
      const cached = cacheUtils.get(cacheKey)
      
      if (cached) {
        commit('SET_RESULTS', cached)
        return cached
      }
      
      try {
        commit('SET_LOADING', true)
        const response = await api.get('/results/detailed/')
        const data = response.data.results || response.data
        
        cacheUtils.set(cacheKey, data)
        commit('SET_RESULTS', data)
        commit('SET_LAST_UPDATE', { key: 'results', timestamp: Date.now() })
        
        return data
      } catch (error) {
        commit('SET_ERROR', 'Помилка завантаження результатів')
        logger.error('Error fetching results:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // Invalidate cache when data changes
    invalidateCache(_, pattern) {
      cacheUtils.invalidate(pattern)
    },
    
    clearAllCache() {
      cacheUtils.clear()
    },
    
    async login({ commit, dispatch }, credentials) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await api.post('/token/', credentials)
        
        localStorage.setItem('access_token', response.data.access)
        localStorage.setItem('refresh_token', response.data.refresh)
        
        commit('SET_AUTHENTICATED', true)
        
        // Fetch current user
        await dispatch('fetchCurrentUser')
        
        // Fetch version info
        await dispatch('fetchVersionInfo')
        
        // Initialize SPA after login
        await dispatch('initializeSPA')
        
        return response
      } catch (error) {
        let errorMessage = 'Помилка входу в систему'
        
        if (error.response?.data) {
          const errorData = error.response.data
          if (errorData.detail) {
            errorMessage = errorData.detail
          } else if (errorData.message) {
            errorMessage = errorData.message
          } else if (errorData.non_field_errors) {
            errorMessage = errorData.non_field_errors[0]
          } else if (typeof errorData === 'string') {
            errorMessage = errorData
          }
        } else if (error.message) {
          errorMessage = error.message
        }
        
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async logout({ commit, dispatch }) {
      // Stop auto-refresh
      dispatch('stopAutoRefresh')
      
      // Clear cache
      dispatch('clearAllCache')
      
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      commit('SET_AUTHENTICATED', false)
      commit('SET_CURRENT_USER', null)
    },
    
    async fetchCurrentUser({ commit }) {
      try {
        const response = await api.get('/users/profile/')
        commit('SET_CURRENT_USER', response.data)
      } catch (error) {
        logger.error('Error fetching current user:', error)
      }
    },

    async fetchVersionInfo({ commit }) {
      try {
        const response = await api.get('/version/')
        commit('SET_VERSION_INFO', response.data)
      } catch (error) {
        logger.error('Error fetching version info:', error)
        // Використовуємо fallback значення якщо API недоступне
        commit('SET_VERSION_INFO', {
          version: 'v0.2.2-alpha',
          build_date: '2025-08-10'
        })
      }
    },

    async updateProfile({ commit }, profileData) {
      try {
        commit('SET_LOADING', true)
        const response = await api.patch('/users/profile/', profileData)
        commit('SET_CURRENT_USER', response.data)
        commit('SET_ERROR', null)
        return response
      } catch (error) {
        commit('SET_ERROR', 'Не вдалося оновити профіль')
        logger.error('Error updating profile:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async changePassword({ commit, state }, passwords) {
      try {
        commit('SET_LOADING', true)
        const response = await api.post(`/users/${state.currentUser.id}/change_password/`, passwords)
        commit('SET_ERROR', null)
        return response
      } catch (error) {
        const message = error.response?.data?.detail || 'Не вдалося змінити пароль'
        commit('SET_ERROR', message)
        logger.error('Error changing password:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchRecentActivities({ commit, state }) {
      try {
        // Перевіряємо, чи користувач авторизований
        if (!state.isAuthenticated) {
          return
        }
        
        const response = await api.get('/users/activity/')
        commit('SET_RECENT_ACTIVITIES', response.data.results || response.data)
      } catch (error) {
        if (error.response?.status === 404) {
          // API endpoint для активності не знайдено
        } else if (error.response?.status === 403) {
          // Немає прав для доступу до активності
        } else {
          logger.error('Помилка завантаження активності:', error)
        }
        commit('SET_RECENT_ACTIVITIES', [])
        throw error
      }
    },
    
    async fetchTests({ commit }) {
      try {
        commit('SET_LOADING', true)
        const response = await api.get('/test-types/')
        commit('SET_TESTS', response.data.results || response.data)
      } catch (error) {
        commit('SET_ERROR', 'Помилка завантаження тестів')
        logger.error('Error fetching tests:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchTestTypes({ commit }) {
      try {
        commit('SET_LOADING', true)
        const response = await api.get('/test-types/')
        commit('SET_TEST_TYPES', response.data.results || response.data)
      } catch (error) {
        commit('SET_ERROR', 'Помилка завантаження типів тестів')
        logger.error('Error fetching test types:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchSessions({ commit }) {
      try {
        commit('SET_LOADING', true)
        const response = await api.get('/sessions/')
        commit('SET_SESSIONS', response.data.results || response.data)
      } catch (error) {
        commit('SET_ERROR', 'Помилка завантаження сесій')
        logger.error('Error fetching sessions:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchSession({ commit }, sessionId) {
      try {
        commit('SET_LOADING', true)
        const response = await api.get(`/sessions/${sessionId}/`)
        return response.data
      } catch (error) {
        commit('SET_ERROR', 'Помилка завантаження сесії')
        logger.error('Error fetching session:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchSessionQuestions({ commit }, sessionId) {
      try {
        commit('SET_LOADING', true)
        const response = await api.get(`/sessions/${sessionId}/questions/`)
        return response.data
      } catch (error) {
        commit('SET_ERROR', 'Помилка завантаження питань')
        logger.error('Error fetching session questions:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchResults({ commit }) {
      try {
        commit('SET_LOADING', true)
        const response = await api.get('/results/detailed/')
        commit('SET_RESULTS', response.data.results || response.data)
      } catch (error) {
        commit('SET_ERROR', 'Помилка завантаження результатів')
        logger.error('Error fetching results:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchUsers({ commit }) {
      const response = await api.get('/users/')
      const usersData = response.data.results || response.data
      commit('SET_USERS', usersData)
      return usersData
    },
    
    async submitTest({ commit }, testData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await api.post('/submit/', testData)
        
        return response
      } catch (error) {
        const errorData = error.response?.data
        let errorMessage = 'Помилка відправки тесту'
        
        if (errorData) {
          // Обробляємо структуру помилки (може бути масив або об'єкт)
          let message = null
          let detail = null
          
          if (Array.isArray(errorData.message)) {
            message = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            message = errorData.message
          }
          
          if (Array.isArray(errorData.detail)) {
            detail = errorData.detail[0]
          } else if (typeof errorData.detail === 'string') {
            detail = errorData.detail
          }
          
          if (message) {
            errorMessage = message
          } else if (detail) {
            errorMessage = detail
          }
          
          // Логуємо детальну інформацію про помилку
          logger.error('Test submission error:', {
            error_code: errorData.error_code,
            detail: detail,
            message: message,
            status: error.response?.status
          })
        }
        
        commit('SET_ERROR', errorMessage)
        
        // Зберігаємо повну структуру помилки для компонентів
        if (error.response?.data) {
          error.userMessage = errorMessage
        }
        
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async submitENPSTest({ commit }, testData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await api.post('/enps-submit/', testData)
        
        return response
      } catch (error) {
        logger.error('=== STORE: ПОМИЛКА eNPS ===')
        logger.error('Error:', error)
        logger.error('Status:', error.response?.status)
        logger.error('Data:', error.response?.data)
        
        const errorData = error.response?.data
        let errorMessage = 'Помилка відправки eNPS тесту'
        
        if (errorData) {
          // Обробляємо структуру помилки (може бути масив або об'єкт)
          let message = null
          let detail = null
          
          if (Array.isArray(errorData.message)) {
            message = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            message = errorData.message
          }
          
          if (Array.isArray(errorData.detail)) {
            detail = errorData.detail[0]
          } else if (typeof errorData.detail === 'string') {
            detail = errorData.detail
          }
          
          if (message) {
            errorMessage = message
          } else if (detail) {
            errorMessage = detail
          }
          
          // Логуємо детальну інформацію про помилку
          logger.error('eNPS test submission error:', {
            error_code: errorData.error_code,
            detail: detail,
            message: message,
            status: error.response?.status
          })
        }
        
        commit('SET_ERROR', errorMessage)
        
        // Зберігаємо повну структуру помилки для компонентів
        if (error.response?.data) {
          error.userMessage = errorMessage
        }
        
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async createSession({ commit }, sessionData) {
      try {
        commit('SET_LOADING', true)
        const response = await api.post('/sessions/', sessionData)
        commit('SET_ERROR', null)
        return response.data
      } catch (error) {
        logger.error('Error creating session:', error)
        let errorMessage = 'Не вдалося створити сесію'
        
        if (error.response?.data) {
          const errorData = error.response.data
          if (Array.isArray(errorData.message)) {
            errorMessage = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message
          } else if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail[0]
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail
          }
        }
        
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Test Type Management
    async activateTestType({ commit }, testTypeId) {
      try {
        commit('SET_LOADING', true)
        const response = await api.post(`/test-types/${testTypeId}/activate/`)
        commit('SET_ERROR', null)
        return response.data
      } catch (error) {
        logger.error('Error activating test type:', error)
        let errorMessage = 'Не вдалося активувати тип тесту'
        
        if (error.response?.data) {
          const errorData = error.response.data
          if (Array.isArray(errorData.message)) {
            errorMessage = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message
          }
        }
        
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async deactivateTestType({ commit }, testTypeId) {
      try {
        commit('SET_LOADING', true)
        const response = await api.post(`/test-types/${testTypeId}/deactivate/`)
        commit('SET_ERROR', null)
        return response.data
      } catch (error) {
        logger.error('Error deactivating test type:', error)
        let errorMessage = 'Не вдалося деактивувати тип тесту'
        
        if (error.response?.data) {
          const errorData = error.response.data
          if (Array.isArray(errorData.message)) {
            errorMessage = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message
          }
        }
        
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Test Session Management
    async activateSession({ commit }, sessionId) {
      try {
        commit('SET_LOADING', true)
        const response = await api.post(`/sessions/${sessionId}/activate/`)
        commit('SET_ERROR', null)
        return response.data
      } catch (error) {
        logger.error('Error activating session:', error)
        let errorMessage = 'Не вдалося активувати сесію'
        
        if (error.response?.data) {
          const errorData = error.response.data
          if (Array.isArray(errorData.message)) {
            errorMessage = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message
          }
        }
        
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async deactivateSession({ commit }, sessionId) {
      try {
        commit('SET_LOADING', true)
        const response = await api.post(`/sessions/${sessionId}/deactivate/`)
        commit('SET_ERROR', null)
        return response.data
      } catch (error) {
        logger.error('Error deactivating session:', error)
        let errorMessage = 'Не вдалося деактивувати сесію'
        
        if (error.response?.data) {
          const errorData = error.response.data
          if (Array.isArray(errorData.message)) {
            errorMessage = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message
          }
        }
        
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async updateSessionDates({ commit }, { sessionId, startDate, endDate }) {
      try {
        commit('SET_LOADING', true)
        const response = await api.post(`/sessions/${sessionId}/update_dates/`, {
          start_date: startDate,
          end_date: endDate
        })
        commit('SET_ERROR', null)
        return response.data
      } catch (error) {
        logger.error('Error updating session dates:', error)
        let errorMessage = 'Не вдалося оновити дати сесії'
        
        if (error.response?.data) {
          const errorData = error.response.data
          if (Array.isArray(errorData.message)) {
            errorMessage = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message
          }
        }
        
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async addParticipants({ commit }, { sessionId, userIds }) {
      try {
        commit('SET_LOADING', true)
        const response = await api.post(`/sessions/${sessionId}/add_participants/`, {
          user_ids: userIds
        })
        commit('SET_ERROR', null)
        return response.data
      } catch (error) {
        logger.error('Error adding participants:', error)
        let errorMessage = 'Не вдалося додати учасників'
        
        if (error.response?.data) {
          const errorData = error.response.data
          if (Array.isArray(errorData.message)) {
            errorMessage = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message
          }
        }
        
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async removeParticipants({ commit }, { sessionId, userIds }) {
      try {
        commit('SET_LOADING', true)
        const response = await api.post(`/sessions/${sessionId}/remove_participants/`, {
          user_ids: userIds
        })
        commit('SET_ERROR', null)
        return response.data
      } catch (error) {
        logger.error('Error removing participants:', error)
        let errorMessage = 'Не вдалося видалити учасників'
        
        if (error.response?.data) {
          const errorData = error.response.data
          if (Array.isArray(errorData.message)) {
            errorMessage = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message
          }
        }
        
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchSessionParticipants({ commit }, sessionId) {
      try {
        commit('SET_LOADING', true)
        const response = await api.get(`/sessions/${sessionId}/participants/`)
        commit('SET_ERROR', null)
        return response.data
      } catch (error) {
        logger.error('Error fetching session participants:', error)
        let errorMessage = 'Не вдалося завантажити учасників сесії'
        
        if (error.response?.data) {
          const errorData = error.response.data
          if (Array.isArray(errorData.message)) {
            errorMessage = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message
          }
        }
        
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async deleteSession({ commit, dispatch }, sessionId) {
      try {
        const response = await api.delete(`/sessions/${sessionId}/`)
        
        commit('SET_ERROR', null)
        
        // Invalidate cache and refresh sessions
        dispatch('invalidateCache', 'sessions')
        await dispatch('fetchSessionsWithCache')
        
        return { success: true }
      } catch (error) {
        logger.error('=== ERROR DELETING SESSION ===')
        logger.error('Session ID:', sessionId)
        logger.error('Error:', error)
        logger.error('Status:', error.response?.status)
        logger.error('Data:', error.response?.data)
        
        let errorMessage = 'Не вдалося видалити сесію'
        
        if (error.response?.data) {
          const errorData = error.response.data
          if (Array.isArray(errorData.message)) {
            errorMessage = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message
          } else if (errorData.detail) {
            errorMessage = errorData.detail
          }
        }
        
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async updateSessionName({ commit, dispatch }, { sessionId, name }) {
      try {
        const response = await api.patch(`/sessions/${sessionId}/`, {
          name: name
        })
        
        commit('SET_ERROR', null)
        
        // Invalidate cache and refresh sessions
        dispatch('invalidateCache', 'sessions')
        await dispatch('fetchSessionsWithCache')
        
        return response.data
      } catch (error) {
        logger.error('=== ERROR UPDATING SESSION NAME ===')
        logger.error('Session ID:', sessionId)
        logger.error('New name:', name)
        logger.error('Error:', error)
        logger.error('Status:', error.response?.status)
        logger.error('Data:', error.response?.data)
        
        let errorMessage = 'Не вдалося оновити назву сесії'
        
        if (error.response?.data) {
          const errorData = error.response.data
          if (Array.isArray(errorData.message)) {
            errorMessage = errorData.message[0]
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message
          } else if (errorData.detail) {
            errorMessage = errorData.detail
          } else if (errorData.name) {
            errorMessage = errorData.name[0]
          }
        }
        
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // Theme management
    changeTheme({ commit }, theme) {
      commit('SET_THEME', theme)
      return theme
    }
  },
  
  getters: {
    isAdmin: state => state.currentUser?.role === 'admin',
    availableTests: state => state.tests.filter(test => test.is_active),
    activeSessions: state => state.sessions.filter(session => session.status === 'active'),
    completedSessions: state => state.sessions.filter(session => session.status === 'completed'),
    // SPA getters
    isOnline: state => state.isOnline,
    autoRefreshEnabled: state => state.autoRefresh,
    lastUpdateTime: state => key => state.lastUpdate[key] || null,
    hasPendingActions: state => state.pendingActions.length > 0,
    cacheStats: () => ({
      size: cache.data.size,
      maxSize: cache.maxSize,
      maxAge: cache.maxAge
    })
  }
}) 